import type { NavLink } from '@/content/types';
import { seoConfig, type SeoConfig } from '@/config/seo';
import { getReadableTextColor } from '@/lib/color';
import type { LocaleInfo } from '@/i18n/types';
import logo from '@/assets/homepage/logo.png';

/**
 * ============================================================================
 *  SITE CONFIG — the single source of truth for branding & theming.
 * ============================================================================
 *
 * This is the *one* file a buyer edits to reskin the template: brand name,
 * tagline, logo, colours, fonts, social links, contact info, and which page
 * sections are enabled.
 *
 * Colours and fonts declared here are injected into the CSS custom properties
 * defined in `src/styles/theme.css` at runtime via `applyTheme()` (called from
 * `src/main.tsx`), so changing a value here reskins the whole site — no need to
 * hunt through components or stylesheets.
 */

export type ThemeMode = 'light' | 'dark' | 'auto';

/** A section that the home page can render, in order. */
export type SectionKey = 'hero' | 'about' | 'origins' | 'products' | 'quality' | 'faq' | 'contact';

export interface SocialLink {
    /** Accessible label, e.g. "LinkedIn". */
    label: string;
    /** Full URL to the profile/page. */
    href: string;
    /**
     * Icon identifier for this link. The self-hosted `src/lib/icons.ts`
     * registry (`lucide-react`) does not ship brand/social logos, so this is
     * a plain `string` rather than the shared `IconName` type — populate it
     * with whatever brand-icon set/component you add for social links (e.g.
     * `react-icons/fa6`, or raw inline SVGs), keyed by platform.
     */
    icon: string;
}

export interface ThemeColors {
    /** Primary brand colour (headings, links, primary UI). */
    primary: string;
    /** Accent/CTA colour (buttons, highlights, icons). */
    accent: string;
    /** Main page surface/background colour. */
    surface: string;
}

/** Name of one of the built-in `THEME_PRESETS` colour palettes. */
export type ThemePresetName = keyof typeof THEME_PRESETS;

/**
 * Prebuilt colour presets — a template-marketplace staple. Instead of
 * picking `primary`/`accent`/`surface` from scratch, a buyer can select one
 * of these ready-made, contrast-checked palettes (see §11 of
 * `docs/TEMPLATE_IMPROVEMENT_RECOMMENDATIONS.md`).
 *
 * Usage: `theme: { colors: THEME_PRESETS.ocean, ... }`, or spread/override a
 * single channel: `theme: { colors: { ...THEME_PRESETS.ocean, accent: '#000' } }`.
 * `applyTheme()` doesn't care where `colors` came from — presets are just a
 * convenient, named starting point for the same `ThemeColors` shape.
 */
export const THEME_PRESETS = {
    /** The original Brilliance Solutions palette — blue + orange. */
    default: { primary: '#2464ac', accent: '#ff6b00', surface: '#ffffff' },
    /** Cool blues/teals — finance, logistics, tech. */
    ocean: { primary: '#0f5b78', accent: '#00b4a6', surface: '#ffffff' },
    /** Greens — agriculture, sustainability, wellness. */
    forest: { primary: '#1f5c3a', accent: '#8bc34a', surface: '#ffffff' },
    /** Warm oranges/reds — food, hospitality, retail. */
    sunset: { primary: '#8a2e2e', accent: '#f4a300', surface: '#fffaf3' },
    /** Purples — creative studios, SaaS, portfolios. */
    berry: { primary: '#4a2b6b', accent: '#e0357b', surface: '#ffffff' },
    /** Neutral greys with a single accent — minimal, editorial. */
    slate: { primary: '#2b333a', accent: '#3d8bfd', surface: '#f7f7f8' },
} satisfies Record<string, ThemeColors>;

export interface ThemeFonts {
    /** Body font stack. */
    base: string;
    /** Heading / logo font stack. */
    heading: string;
}

export interface SiteConfig {
    brand: {
        /** Full brand name (used in the footer, SEO, alt text). */
        name: string;
        /** Two-tone logo text rendered in the navbar. */
        words: { first: string; second: string };
        /** Imported logo asset (swap the file in `src/assets`). */
        logo: string;
        /** Alt text for the logo image. */
        logoAlt: string;
        /** Short marketing tagline. */
        tagline: string;
    };
    theme: {
        /** `auto` follows the OS setting; `light`/`dark` force a palette. */
        mode: ThemeMode;
        /**
         * The active colour palette. Pick one of `THEME_PRESETS` (e.g.
         * `THEME_PRESETS.ocean`) or supply fully custom values — both are
         * plain `ThemeColors` objects, so `applyTheme()` treats them
         * identically.
         */
        colors: ThemeColors;
        fonts: ThemeFonts;
    };
    contact: {
        /** Human-readable location shown in the footer. */
        location: string;
        /** Copyright/rights notice shown in the footer. */
        rights: string;
    };
    /** Social profiles (empty array hides the list). */
    social: SocialLink[];
    /** Primary navigation links (anchor to section ids). */
    navLinks: NavLink[];
    /** The home-page sections to render, in the desired order. */
    sections: SectionKey[];
    /**
     * Extra standalone pages (About, Blog, ...) rendered by
     * `react-router` routes in `App.tsx`. Rendered in the navbar *after* the
     * section anchors. Leave empty to ship a classic one-pager. See §11 of
     * `docs/TEMPLATE_IMPROVEMENT_RECOMMENDATIONS.md`.
     */
    pages: { path: string; label: string }[];
    /**
     * Optional i18n scaffold (see `src/i18n/`). Disabled by default so a
     * fresh clone behaves exactly like a template with no i18n at all;
     * flip `enabled` to `true` to show the `LanguageSwitcher` in the navbar.
     */
    i18n: {
        enabled: boolean;
        /** Locale used when no locale is stored yet and as the translation fallback. */
        defaultLocale: string;
        /** Locales offered in the `LanguageSwitcher`; each `code` must have a matching dictionary in `src/i18n/dictionaries.ts`. */
        locales: LocaleInfo[];
    };
    /**
     * SEO / metadata & structured data. Defined in its own pure module
     * (`src/config/seo.ts`) so it can also drive `robots.txt` / `sitemap.xml`
     * generation at build time.
     */
    seo: SeoConfig;
    /**
     * Opt-in Vercel telemetry. Both are no-ops (and add zero bundle weight
     * beyond a tiny script loader) unless the project is actually deployed on
     * Vercel, so they are safe to leave enabled for buyers who deploy there.
     */
    analytics: {
        /** Toggles `@vercel/analytics` (privacy-friendly page-view analytics). */
        enabled: boolean;
        /** Toggles `@vercel/speed-insights` (Core Web Vitals reporting). */
        speedInsights: boolean;
    };
}

export const siteConfig: SiteConfig = {
    brand: {
        name: 'Brilliance Solutions',
        words: {
            first: 'Brilliance',
            second: 'Solutions',
        },
        logo,
        logoAlt: 'Brilliance Solutions Logo',
        tagline: 'Premium Global Meat Import & Distribution',
    },
    theme: {
        mode: 'light',
        // Swap this for any other key of `THEME_PRESETS` (e.g. `THEME_PRESETS.ocean`)
        // to reskin the whole site with one line, or provide custom `ThemeColors`.
        colors: THEME_PRESETS.default,
        fonts: {
            base: "'Segoe UI', Tahoma, sans-serif",
            heading: "'Georgia', serif",
        },
    },
    contact: {
        location: 'Riyadh, Saudi Arabia',
        rights: 'All Rights Reserved.',
    },
    social: [],
    navLinks: [{ id: 'home', label: 'Home' }] satisfies NavLink[],
    sections: ['hero', 'about', 'products', 'origins', 'quality', 'faq', 'contact'],
    pages: [
        { path: '/company', label: 'Company' },
        { path: '/products', label: 'Products' },
        { path: '/origins', label: 'Origins' },
        { path: '/quality', label: 'Quality' },
        { path: '/blog', label: 'Blog' },
        { path: '/contact', label: 'Contact Us' },
    ],
    i18n: {
        enabled: false,
        defaultLocale: 'en',
        locales: [
            { code: 'en', label: 'English' },
            { code: 'es', label: 'Español' },
        ],
    },
    seo: seoConfig,
    analytics: {
        enabled: true,
        speedInsights: true,
    },
};

/**
 * Push the configured brand colours and fonts into the CSS custom properties
 * declared in `src/styles/theme.css`, and apply the selected theme mode.
 *
 * Call once on startup (see `src/main.tsx`). Safe to no-op outside the browser
 * (e.g. during SSR/tests) where `document` is unavailable.
 */
export function applyTheme(config: SiteConfig = siteConfig): void {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const { colors, fonts, mode } = config.theme;

    root.style.setProperty('--brand-primary', colors.primary);
    root.style.setProperty('--brand-accent', colors.accent);
    root.style.setProperty('--brand-surface', colors.surface);
    root.style.setProperty('--font-base', fonts.base);
    root.style.setProperty('--font-heading', fonts.heading);

    // Auto-pick black/white text so buttons/badges rendered on top of these
    // brand colours always meet WCAG contrast, whatever colour a buyer picks
    // (see `src/lib/color.ts` and `docs/TEMPLATE_IMPROVEMENT_RECOMMENDATIONS.md` §9).
    root.style.setProperty('--brand-primary-contrast', getReadableTextColor(colors.primary));
    root.style.setProperty('--brand-accent-contrast', getReadableTextColor(colors.accent));

    if (mode === 'auto') {
        root.removeAttribute('data-theme');
    } else {
        root.setAttribute('data-theme', mode);
    }

    // Keep the browser UI (address bar) in sync with the brand colour.
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', colors.primary);
}
